import React from 'react';
import { useNavigate } from 'react-router-dom';
import WikiIcon from '../shared/WikiIcon';
import { wikiPaths } from '../shared/wikiRoutes';
import { ASK_COPY } from './askCopy';
import { askStore, useAskStore } from './askStore';

/**
 * Source cards under an answer (D-ask §1.3(2)): number chip · page name ·
 * version label, the "Category › Subcategory › Section" path, and a collapsed
 * "Show exact wording" box with the cited passage exactly as written. The
 * card opens the page; the toggle does not.
 */

function openOnKey(event, open) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
    }
}

export function versionText(source) {
    if (source?.version_label) return source.version_label;
    if (source?.version !== undefined && source?.version !== null && source?.version !== '') return `v${source.version}`;
    return '';
}

export function SourceCard({ source, turnId, readOnly = false }) {
    const exactKey = `${turnId}-${source?.n}`;
    const exactOpen = useAskStore((s) => Boolean(s.exact[exactKey]));
    const navigate = useNavigate();

    const pageId = source?.page_id;
    const open = () => { if (pageId) navigate(wikiPaths.page(pageId)); };
    const version = versionText(source);
    const hasExact = !readOnly && Boolean(source?.exact);
    const showChip = source?.n !== undefined && source?.n !== null;

    return (
        <div
            className="wk-ask-source"
            role="link"
            tabIndex={0}
            onClick={open}
            onKeyDown={(event) => openOnKey(event, open)}
            data-testid="wiki-ask-source"
            data-page-id={pageId || undefined}
        >
            <div className="wk-ask-source-row">
                {showChip && <span className="wk-ask-cite wk-ask-cite--card">{source.n}</span>}
                <span className="wk-ask-source-name">{source?.name || 'Untitled page'}</span>
                {version && <span className="wk-ask-source-version">{version}</span>}
            </div>
            {source?.path && <div className="wk-ask-source-path">{source.path}</div>}
            {hasExact && (
                <button
                    type="button"
                    className="wk-ask-exact-toggle"
                    aria-expanded={exactOpen}
                    onClick={(event) => { event.stopPropagation(); askStore.toggleExact(turnId, source.n); }}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <WikiIcon name="eye" size={12} />
                    {exactOpen ? ASK_COPY.hideExact : ASK_COPY.showExact}
                </button>
            )}
            {hasExact && exactOpen && (
                <div className="wk-ask-exact" data-testid="wiki-ask-exact">
                    <div className="wk-ask-exact-label">{ASK_COPY.exactLabel}</div>
                    <div className="wk-ask-exact-text">{source.exact}</div>
                </div>
            )}
        </div>
    );
}

export function SourceGrid({ sources, turnId, readOnly = false }) {
    if (!Array.isArray(sources) || sources.length === 0) return null;
    return (
        <div className="wk-ask-sources" data-testid="wiki-ask-sources">
            {sources.map((source, index) => (
                <SourceCard
                    key={`${source?.n ?? 'x'}-${source?.page_id || index}`}
                    source={source}
                    turnId={turnId}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
}

/** Gap card: the closest page the search found (opens it). */
export function ClosestPageCard({ closest }) {
    const navigate = useNavigate();
    if (!closest || !closest.page_id) return null;
    const open = () => navigate(wikiPaths.page(closest.page_id));
    return (
        <div className="wk-ask-sources wk-ask-sources--closest">
            <div
                className="wk-ask-source"
                role="link"
                tabIndex={0}
                onClick={open}
                onKeyDown={(event) => openOnKey(event, open)}
                data-testid="wiki-ask-closest"
                data-page-id={closest.page_id}
            >
                <div className="wk-ask-source-row">
                    <WikiIcon name="file-text" size={13} className="wk-ask-source-icon" />
                    <span className="wk-ask-source-name">{closest.name || 'Untitled page'}</span>
                    <span className="wk-ask-source-version">{ASK_COPY.closestPage}</span>
                </div>
                {closest.path && <div className="wk-ask-source-path">{closest.path}</div>}
            </div>
        </div>
    );
}
